import { SalonService } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import { formatCommissionAmount } from "../../../utils/commissionUtils";
import { formatCurrency } from "../../../utils/cashDrawerUtils";

/**
 * Builds the "service menu" the commission drawer shows: the services a staff
 * member performs, grouped by category, each with its commission. Shared by
 * the read-only menu (Current / Past rates) and the "Rates by service" editor.
 */

/** Group heading for services without a category. */
export const UNCATEGORIZED = "Other services";
/** Group heading for rates kept for services the staff member no longer does. */
export const NO_LONGER_OFFERED = "No longer offered";

export interface ServiceGroup<T> {
  category: string;
  items: T[];
}

/** Services this staff member performs (all of them for a tier template). */
export const servicesFor = (
  services: SalonService[],
  staffId: string,
): SalonService[] =>
  staffId
    ? services.filter((s) =>
        (s.servicing_staff || []).some((ss) => ss.staff_id === staffId),
      )
    : services;

/** Services grouped by category (A–Z), services A–Z within each group;
 * uncategorized services last. */
export const groupByCategory = (
  services: SalonService[],
): ServiceGroup<SalonService>[] => {
  const groups = new Map<string, SalonService[]>();
  [...services]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((service) => {
      const category = service.service_type?.trim() || UNCATEGORIZED;
      groups.set(category, [...(groups.get(category) || []), service]);
    });
  return Array.from(groups.entries())
    .sort(([a], [b]) =>
      a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b),
    )
    .map(([category, items]) => ({ category, items }));
};

/** The staff member's own price and duration for a service, falling back to
 * the service's defaults. */
export const staffServiceTerms = (
  service: SalonService,
  staffId: string,
): { price: number | null; durationMins: number | null } => {
  const own = (service.servicing_staff || []).find(
    (ss) => ss.staff_id === staffId,
  );
  return {
    price: own?.price ?? service.default_price ?? null,
    durationMins:
      own?.base_duration_mins ?? service.default_base_duration_mins ?? null,
  };
};

/** "$220.00 · 180 min" (either part omitted when unknown). */
export const formatServiceMeta = (
  service: SalonService,
  staffId: string,
): string => {
  const { price, durationMins } = staffServiceTerms(service, staffId);
  return [
    price != null ? formatCurrency(price) : null,
    durationMins ? `${durationMins} min` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

export interface ServiceRateRow {
  id: string;
  name: string;
  price: number | null;
  /** "40%" or "$15". */
  rate: string;
}

/**
 * A service-specific record's rates for every service the staff member
 * performs, grouped by category. Services without their own rate earn the
 * record's default (0% when saved from the service-specific screen). Rates
 * kept for services no longer offered are listed in their own group.
 */
export const buildServiceRateMenu = (
  record: CommissionRecord,
  services: SalonService[],
  staffId: string,
): ServiceGroup<ServiceRateRow>[] => {
  const overrides = new Map(
    (record.service_overrides || []).map((o) => [o.salon_service_id, o]),
  );
  const defaultRate =
    formatCommissionAmount(
      record.service_amount ?? 0,
      record.service_amount_type ?? "Percentage",
    ) || "0%";
  const offered = servicesFor(services, staffId);
  const menu = groupByCategory(offered).map(({ category, items }) => ({
    category,
    items: items.map((service) => {
      const own = overrides.get(service.id);
      return {
        id: service.id,
        name: service.name,
        price: staffServiceTerms(service, staffId).price,
        rate: own
          ? formatCommissionAmount(own.amount, own.amount_type) || defaultRate
          : defaultRate,
      };
    }),
  }));
  const offeredIds = new Set(offered.map((s) => s.id));
  const kept = (record.service_overrides || [])
    .filter((o) => !offeredIds.has(o.salon_service_id))
    .sort((a, b) =>
      (a.salon_service_name || "").localeCompare(b.salon_service_name || ""),
    )
    .map((o) => ({
      id: o.salon_service_id,
      name: o.salon_service_name || "Service",
      price: null,
      rate: formatCommissionAmount(o.amount, o.amount_type) || "—",
    }));
  return kept.length > 0
    ? [...menu, { category: NO_LONGER_OFFERED, items: kept }]
    : menu;
};
